'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useContext, useCallback, useRef, useReducer } from 'react';
import PropTypes from 'prop-types';
import NavMarker from 'react-rhumb/NavMarker';
import { useSelector, useDispatch } from 'react-redux';
import Immutable from 'immutable';
import I18n from 'I18n';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import HrefLangLink from 'ecosystem-marketplace-core/components/elements/HrefLangLink';
import MetaDescription from 'ecosystem-marketplace-core/components/elements/MetaDescription';
import MetaOpenGraph from 'ecosystem-marketplace-core/components/elements/MetaOpenGraph';
import MetaTwitter from 'ecosystem-marketplace-core/components/elements/MetaTwitter';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import { isUninitialized, isFailed } from 'ecosystem-marketplace-core/utils/requestState';
import { getIsPrerenderUserAgent } from 'ecosystem-marketplace-core/utils/userAgent';
import { fetchCollectionResult } from '../../actions/AllCollectionsActions';
import { PublicOnly } from '../../selectors/auth';
import { getHasAllFulfilledRequests, getHasAnySucceededRequests, makeGetCollectionItems, makeGetCollectionRequestState } from '../../selectors/allCollections';
import { getDisplayedCollectionListings } from '../../selectors/collections';
import { getFullHref } from '../../utils/history';
import { useMounted } from '../../utils/hooks/useMounted';
import { useSocialScreenshotUrl } from '../../utils/hooks/useSocialScreenshot';
import { useVisible } from '../../utils/hooks/useVisible';
import { getLocalizedPublicUrl } from '../../utils/urls';
import StorefrontSectionMarkerContext from '../../context/StorefrontSectionMarkerContext';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import CanonicalLink from '../../components/elements/CanonicalLink';
import GenericError from '../../components/error/GenericError';
import ResultsGrid from '../../components/storefront/results/ResultsGrid';
import ResultsSection from 'ecosystem-marketplace-core/components/storefront/ResultsSection';
import ResultsSectionHeader from 'ecosystem-marketplace-core/components/storefront/ResultsSectionHeader';
import ValidI18nMessage from 'ecosystem-marketplace-core/i18n/components/ValidI18nMessage';
import StorefrontSectionMarker from '../../components/storefront/StorefrontSectionMarker';
import FeaturedCalloutsSection from '../../components/storefront/FeaturedCalloutsSection';
const LANGS = ['en', 'de', 'es', 'fr', 'ja', 'pt'];
const MIN_SECTION_HEIGHT = 360;

const CollectionResultsSection = ({
  collectionListing,
  lazy = true,
  sectionIndex,
  trackSectionInteraction,
  trackSectionResultClick
}) => {
  const {
    id: listingId,
    collectionId,
    slug: collectionSlug
  } = collectionListing;
  const collectionI18nKey = `collection_${collectionId}`;
  const dispatch = useDispatch();
  const getCollectionItems = useMemo(makeGetCollectionItems, []);
  const getCollectionRequestState = useMemo(makeGetCollectionRequestState, []);
  const results = useSelector(state => getCollectionItems(state, collectionId));
  const requestState = useSelector(state => getCollectionRequestState(state, collectionId));
  const requestUninitialized = isUninitialized(requestState);
  const sectionRef = useRef(null);
  const visible = useVisible(sectionRef, `${MIN_SECTION_HEIGHT / 2}px 0px 0px 0px`);
  const trackingId = `${collectionId}${listingId ? `_${listingId}` : ''}`;
  const sectionName = `collection::${trackingId}::default`;
  const renderedResults = useMemo(() => {
    if (isFailed(requestState)) {
      return /*#__PURE__*/_jsx(GenericError, {});
    }

    return /*#__PURE__*/_jsx(ResultsGrid, {
      onResultClick: (result, index) => {
        trackSectionResultClick(sectionName, result, index, {
          totalResults: results.size,
          filter: trackingId
        });
      },
      requestState: requestState,
      results: results
    });
  }, [requestState, results, sectionName, trackSectionResultClick, trackingId]);

  const renderedDetails = /*#__PURE__*/_jsx(ValidI18nMessage, {
    message: `appMarketplace.collections.${collectionI18nKey}.tagline`,
    defaultValue: ''
  });

  const renderedTitle = /*#__PURE__*/_jsx(ValidI18nMessage, {
    message: `appMarketplace.collections.${collectionI18nKey}.name`,
    defaultValue: collectionListing.name
  });

  useEffect(() => {
    if (requestUninitialized && (!lazy || lazy && visible)) {
      dispatch(fetchCollectionResult(collectionId));
    }
  }, [collectionId, dispatch, lazy, requestUninitialized, visible]);
  return /*#__PURE__*/_jsxs("div", {
    ref: sectionRef,
    children: [/*#__PURE__*/_jsx(StorefrontSectionMarker, {
      name: sectionName,
      placementValue: sectionIndex
    }), /*#__PURE__*/_jsxs(ResultsSection, {
      style: {
        minHeight: MIN_SECTION_HEIGHT
      },
      children: [/*#__PURE__*/_jsx(ResultsSectionHeader, {
        details: renderedDetails,
        title: renderedTitle,
        children: /*#__PURE__*/_jsx(UIRouterLink, {
          onClick: () => {
            trackSectionInteraction(sectionName, 'click see all collection');
          },
          to: `/${collectionSlug}`,
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "appMarketplace.storefront.results.allCollections.exploreCollection",
            options: {
              title: renderedTitle
            }
          })
        })
      }), renderedResults]
    })]
  });
};

const ActionTypes = {
  FEATURED_CALLOUTS_MOUNTED: 'FEATURED_CALLOUTS_MOUNTED',
  SECTION_MOUNTED: 'SECTION_MOUNTED'
};
const initialState = {
  featuredCalloutsMounted: false,
  sections: Immutable.List()
};

const reducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.FEATURED_CALLOUTS_MOUNTED:
      {
        return Object.assign({}, state, {
          featuredCalloutsMounted: true
        });
      }

    case ActionTypes.SECTION_MOUNTED:
      {
        const {
          sections
        } = state;
        const {
          name,
          placementValue
        } = action.payload;
        return Object.assign({}, state, {
          sections: sections.push({
            name,
            placementValue
          })
        });
      }

    default:
      return state;
  }
};

const AllCollections = ({
  match: {
    url
  }
}) => {
  const mounted = useMounted();
  const isPrerenderUserAgent = getIsPrerenderUserAgent();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    featuredCalloutsMounted,
    sections
  } = state;
  const collectionListings = useSelector(getDisplayedCollectionListings);
  const hasAllFulfilledRequests = useSelector(getHasAllFulfilledRequests);
  const hasAnySucceededRequests = useSelector(getHasAnySucceededRequests);
  const sortedSections = useMemo(() => sections.sortBy(({
    placementValue
  }) => placementValue).map(({
    name
  }) => name), [sections]);
  const getSectionWithPlacement = useCallback(section => {
    const index = sortedSections.indexOf(section);
    return `${index < 10 ? `0${index}` : index}::${section}`;
  }, [sortedSections]);
  const {
    trackInteraction,
    trackPageView,
    trackResultClick
  } = useContext(StorefrontUsageTrackerContext);
  const trackSectionInteraction = useCallback((section, action) => {
    trackInteraction(action, {
      section,
      sectionWithPlacement: getSectionWithPlacement(section)
    }, 'storefront-section-interaction');
  }, [getSectionWithPlacement, trackInteraction]);
  const trackSectionResultClick = useCallback((section, result, resultIndex, properties = {}) => {
    trackResultClick(result, resultIndex, Object.assign({}, properties, {
      section,
      sectionWithPlacement: getSectionWithPlacement(section)
    }));
  }, [getSectionWithPlacement, trackResultClick]);
  const handleFeaturedCalloutsMount = useCallback(() => {
    dispatch({
      type: ActionTypes.FEATURED_CALLOUTS_MOUNTED
    });
  }, []);
  const handleSectionMount = useCallback((name, placementValue) => {
    dispatch({
      type: ActionTypes.SECTION_MOUNTED,
      payload: {
        name,
        placementValue
      }
    });
  }, []);
  const renderedResults = useMemo(() => collectionListings.map((collectionListing, index) => {
    const {
      collectionId
    } = collectionListing; // offset index to account for FeaturedCalloutsSection

    const sectionIndex = index + 1;
    return /*#__PURE__*/_jsx(CollectionResultsSection, {
      collectionListing: collectionListing,
      lazy: !(isPrerenderUserAgent || index === 0),
      sectionIndex: sectionIndex,
      trackSectionInteraction: trackSectionInteraction,
      trackSectionResultClick: trackSectionResultClick
    }, collectionId);
  }), [collectionListings, isPrerenderUserAgent, trackSectionInteraction, trackSectionResultClick]);
  const socialImageUrl = useSocialScreenshotUrl();
  const renderedPageMeta = useMemo(() => {
    const metaDescription = unescapedText('appMarketplace.storefront.filters.collection.items.all.metaDescription');
    return /*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(CanonicalLink, {
          to: url
        }), /*#__PURE__*/_jsx(HrefLangLink, {
          href: getLocalizedPublicUrl('en'),
          "x-default": true
        }), LANGS.map(lang => /*#__PURE__*/_jsx(HrefLangLink, {
          href: getLocalizedPublicUrl(lang),
          lang: lang
        }, lang)), /*#__PURE__*/_jsx(MetaDescription, {
          content: metaDescription
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title",
          content: document.title
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: getFullHref(url)
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: metaDescription
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(PrerenderReady, {
          ready: hasAllFulfilledRequests
        })]
      })
    });
  }, [hasAllFulfilledRequests, socialImageUrl, url]);
  useEffect(() => {
    document.title = I18n.text('appMarketplace.storefront.filters.collection.items.all.htmlTitle');
  }, []);
  useEffect(() => {
    if (mounted && featuredCalloutsMounted) {
      trackPageView({
        section: sortedSections.toArray(),
        sectionWithPlacement: sortedSections.map(getSectionWithPlacement).toArray()
      });
    }
  }, [featuredCalloutsMounted, getSectionWithPlacement, mounted, sortedSections, trackPageView]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasAnySucceededRequests && /*#__PURE__*/_jsx(NavMarker, {
      name: "STOREFRONT_LOAD"
    }), /*#__PURE__*/_jsxs(StorefrontSectionMarkerContext.Provider, {
      value: {
        onMount: handleSectionMount
      },
      children: [/*#__PURE__*/_jsx(FeaturedCalloutsSection, {
        onFeaturedCalloutsMount: handleFeaturedCalloutsMount,
        sectionIndex: 0,
        trackSectionInteraction: trackSectionInteraction
      }), renderedResults, renderedPageMeta]
    })]
  });
};

AllCollections.propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string.isRequired
  }).isRequired
};
export default AllCollections;